<template>
  <v-container fluid class="calendar-page pa-3 pa-md-5" :class="{ 'calendar-page--embedded': embedded }">
    <div v-if="!embedded" class="calendar-heading">
      <div>
        <div class="section-kicker">CONTROL OPERATIVO</div>
        <h1>Calendar</h1>
        <p>Contests, events, payment dates and birthdays all in one place.</p>
      </div>
      <v-btn-group divided color="primary" density="compact" class="calendar-add-group">
        <v-btn size="small" prepend-icon="mdi-plus" @click="openCreateDialog('event')">Event</v-btn>
        <v-menu>
          <template #activator="{ props }">
            <v-btn v-bind="props" size="small" icon="mdi-menu-down" aria-label="Choose what to add" />
          </template>
          <v-list density="compact" min-width="190">
            <v-list-item prepend-icon="mdi-trophy-outline" title="Event" @click="openCreateDialog('event')" />
            <v-list-item prepend-icon="mdi-clipboard-check-outline" title="Task" @click="openCreateTaskDialog" />
            <v-list-item prepend-icon="mdi-calendar-star" title="Marathon" @click="openCreateDialog('marathon')" />
          </v-list>
        </v-menu>
      </v-btn-group>
    </div>

    <v-card class="calendar-card" elevation="2">
      <div v-if="embedded" class="embedded-calendar-toolbar">
        <div>
          <v-icon size="18">mdi-calendar-month-outline</v-icon>
          <strong>{{ isModelAccount ? 'Model Calendar' : 'Task Calendar' }}</strong>
        </div>
        <div class="calendar-legend task-status-legend" aria-label="Task status colors">
          <span v-if="isModelAccount"><i class="legend-dot competition"></i>Events</span>
          <span v-if="isModelAccount"><span class="legend-double-dot"><i class="legend-dot payment-main"></i><i class="legend-dot payment-cb"></i></span>Payments</span>
          <span v-if="isModelAccount"><i class="legend-dot birthday"></i>My birthday</span>
          <span><i class="legend-dot task-upcoming"></i>Upcoming</span>
          <span><i class="legend-dot task-today"></i>Due today</span>
          <span><i class="legend-dot task-overdue"></i>Overdue</span>
          <span><i class="legend-dot task-completed"></i>Completed</span>
        </div>
      </div>
      <div v-if="!embedded" class="calendar-toolbar">
        <div class="calendar-scope-controls">
          <div class="calendar-view-toggle">
            <button
              type="button"
              class="calendar-mode-button"
              :class="{ 'is-selected': !contentFilters.tasks }"
              @click="showStandardCalendar"
            >
              <v-icon size="16">mdi-calendar-month-outline</v-icon>
              Calendar
            </button>
            <button
              type="button"
              class="calendar-mode-button"
              :class="{ 'is-selected': contentFilters.tasks }"
              @click="showTasksCalendar"
            >
              <v-icon size="16">mdi-checkbox-marked-circle-outline</v-icon>
              Tasks
            </button>
          </div>
          <div class="calendar-scope-group">
            <span>Model Type</span>
            <v-btn-toggle v-model="modelTypeFilter" density="compact" color="primary" @update:model-value="handleModelTypeChange">
              <v-btn class="calendar-scope-icon-button" value="both" aria-label="Studio and Satellite" title="Studio + Satellite">
                <v-icon size="18">mdi-layers-outline</v-icon>
              </v-btn>
              <v-btn value="studio">Studio</v-btn>
              <v-btn value="sat">Satellite</v-btn>
            </v-btn-toggle>
          </div>
          <div class="calendar-scope-group">
            <span>Studio</span>
            <v-btn-toggle v-model="studioFilter" density="compact" color="primary" @update:model-value="handleStudioChange">
              <v-btn class="calendar-scope-icon-button" value="all" aria-label="All studios" title="All studios">
                <v-icon size="18">mdi-layers-triple-outline</v-icon>
              </v-btn>
              <v-btn v-for="studio in studios" :key="studio.id" :value="String(studio.id)">
                {{ studio.name }}
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>

        <div v-if="!contentFilters.tasks" class="calendar-legend">
          <button type="button" class="calendar-content-filter" :class="{ 'is-selected': contentFilters.events }" :aria-pressed="contentFilters.events" @click="toggleContentFilter('events')">
            <i class="legend-dot competition"></i>Events
          </button>
          <button type="button" class="calendar-content-filter" :class="{ 'is-selected': contentFilters.payments }" :aria-pressed="contentFilters.payments" @click="toggleContentFilter('payments')">
            <span class="legend-double-dot"><i class="legend-dot payment-main"></i><i class="legend-dot payment-cb"></i></span>Payments
          </button>
          <button type="button" class="calendar-content-filter" :class="{ 'is-selected': contentFilters.birthdays }" :aria-pressed="contentFilters.birthdays" @click="toggleContentFilter('birthdays')">
            <i class="legend-dot birthday"></i>Birthdays
          </button>
        </div>
        <div v-else class="calendar-legend task-status-legend" aria-label="Task status colors">
          <span><i class="legend-dot task-upcoming"></i>Upcoming</span>
          <span><i class="legend-dot task-today"></i>Due today</span>
          <span><i class="legend-dot task-overdue"></i>Overdue</span>
          <span><i class="legend-dot task-completed"></i>Completed</span>
        </div>
      </div>

      <div v-if="!embedded && contentFilters.tasks" class="calendar-task-audience-strip">
        <span class="calendar-model-label">TASKS</span>
        <div class="calendar-view-toggle">
          <button type="button" class="calendar-mode-button" :class="{ 'is-selected': taskAudienceFilter === 'all' }" @click="selectTaskAudience('all')">All Tasks</button>
          <button type="button" class="calendar-mode-button" :class="{ 'is-selected': taskAudienceFilter === 'models' }" @click="selectTaskAudience('models')">
            <v-icon size="15">mdi-account-star-outline</v-icon>Models
          </button>
          <button type="button" class="calendar-mode-button" :class="{ 'is-selected': taskAudienceFilter === 'monitors' }" @click="selectTaskAudience('monitors')">
            <v-icon size="15">mdi-account-tie-outline</v-icon>Monitors
          </button>
          <button v-if="adminChipOptions.length" type="button" class="calendar-mode-button" :class="{ 'is-selected': taskAudienceFilter === 'admin' }" @click="selectTaskAudience('admin')">
            <v-icon size="15">mdi-shield-account-outline</v-icon>Admin
          </button>
        </div>
      </div>

      <v-alert v-if="errorMessage" type="error" variant="tonal" class="ma-4 mb-0">
        {{ errorMessage }}
      </v-alert>

      <div v-if="!embedded && showModelTray && (!contentFilters.tasks || taskAudienceFilter === 'models')" class="calendar-model-strip">
        <span class="calendar-model-label">
          {{ modelTypeFilter === 'sat' ? 'SATELLITE' : modelTypeFilter === 'studio' ? 'STUDIO' : 'ALL' }}<br>
          MODEL
        </span>
        <div class="calendar-model-pills">
          <button
            type="button"
            class="calendar-model-pill"
            :class="{ 'is-selected': calendarMode === 'models' && modelPreviewSelection === 'all' }"
            @click="selectModelCalendar('all')"
          >
            All Models
          </button>
          <button
            v-for="model in modelChipOptions"
            :key="model.id"
            type="button"
            class="calendar-model-pill"
            :class="{ 'is-selected': calendarMode === 'models' && modelPreviewSelection === model.id }"
            @click="selectModelCalendar(model)"
          >
            {{ model.label }}
          </button>
        </div>
      </div>

      <div v-if="!embedded && contentFilters.tasks && taskAudienceFilter === 'monitors'" class="calendar-model-strip">
        <span class="calendar-model-label">MONITOR</span>
        <div class="calendar-model-pills">
          <button type="button" class="calendar-model-pill" :class="{ 'is-selected': monitorPreviewSelection === 'all' }" @click="selectMonitorCalendar('all')">All Monitors</button>
          <button
            v-for="monitor in monitorChipOptions"
            :key="monitor.id"
            type="button"
            class="calendar-model-pill"
            :class="{ 'is-selected': monitorPreviewSelection === monitor.id }"
            @click="selectMonitorCalendar(monitor)"
          >{{ monitor.label }}</button>
        </div>
      </div>

      <div v-if="!embedded && contentFilters.tasks && taskAudienceFilter === 'admin'" class="calendar-model-strip">
        <span class="calendar-model-label">ADMIN</span>
        <div class="calendar-model-pills">
          <button type="button" class="calendar-model-pill" :class="{ 'is-selected': adminPreviewSelection === 'all' }" @click="selectAdminCalendar('all')">All Admin</button>
          <button
            v-for="admin in adminChipOptions"
            :key="admin.id"
            type="button"
            class="calendar-model-pill"
            :class="{ 'is-selected': adminPreviewSelection === admin.id }"
            @click="selectAdminCalendar(admin)"
          >{{ admin.label }}</button>
        </div>
      </div>

      <div class="calendar-wrap" :class="{ 'is-loading': loading }">
        <vue-cal
          ref="calendar"
          class="modato-calendar"
          :class="{ 'is-model-calendar': isModelEventContext }"
          active-view="month"
          :disable-views="['years', 'year', 'week', 'day']"
          :events="calendarEvents"
          :time="false"
          :selected-date="selectedDate"
          hide-view-selector
          today-button
          events-on-month-view="short"
          @event-click="openEvent"
          @cell-click="handleCellClick"
          @view-change="handleViewChange"
        >
          <template #arrow-prev>
            <v-icon size="20">mdi-chevron-left</v-icon>
          </template>
          <template #arrow-next>
            <v-icon size="20">mdi-chevron-right</v-icon>
          </template>
          <template #today-button>
            <span>Today</span>
          </template>
          <template #event="{ event }">
            <div
              v-if="event.source !== 'spacer'"
              class="calendar-event-content"
              :title="event.source === 'model-task' ? `${event.model_name}: ${event.title}` : event.source === 'monitor-task' ? `${event.monitor_name}: ${event.title}` : event.source === 'admin-task' ? `${adminDisplayName(event.assigned_user_email)}: ${event.title}` : event.title"
              :style="eventContentStyle(event)"
            >
              <v-icon
                v-if="isTaskEvent(event) && event.completed"
                class="calendar-event-icon completed-task-icon"
                size="14"
              >mdi-check-circle</v-icon>
              <v-icon v-if="event.source === 'monitor-task' && !event.completed" class="calendar-event-icon" size="14">mdi-account-tie-outline</v-icon>
              <v-icon v-if="event.source === 'admin-task' && !event.completed" class="calendar-event-icon" size="14">mdi-shield-account-outline</v-icon>
              <span class="calendar-event-title">{{ event.title }}</span>
              <v-icon
                v-if="event.source === 'birthday-model' || event.source === 'birthday-monitor'"
                class="calendar-birthday-icon"
                size="14"
              >mdi-cake-variant</v-icon>
              <v-icon
                v-if="event.source === 'payment-main' || event.source === 'payment-cb'"
                class="calendar-payment-icon"
                size="14"
              >mdi-bank</v-icon>
            </div>
          </template>
        </vue-cal>
        <v-progress-linear v-if="loading" indeterminate color="primary" class="calendar-loader" />
      </div>
    </v-card>

    <v-dialog v-model="eventDialog" max-width="620">
      <v-card>
        <v-card-title class="dialog-title">
          {{ editingEventId ? 'Edit Event' : 'Add Event' }}
        </v-card-title>
        <v-card-text class="pt-5">
          <v-text-field v-model="form.title" label="Title" variant="outlined" density="compact" />
          <div class="form-grid">
            <v-select
              v-model="form.event_type"
              :items="eventTypes"
              item-title="name"
              item-value="value"
              label="Type"
              variant="outlined"
              density="compact"
            />
            <v-select
              v-model="form.website_id"
              :items="siteDropdownItems"
              item-title="name"
              item-value="id"
              label="Site (optional)"
              variant="outlined"
              density="compact"
              clearable
            >
              <template #item="{ props, item }">
                <div v-if="item.raw.isDivider" class="site-select-divider" aria-hidden="true"></div>
                <v-list-item v-else v-bind="props" />
              </template>
            </v-select>
            <v-select
              v-if="isModelEventContext"
              class="form-grid-full"
              v-model="form.model_id"
              :items="modelChipOptions"
              item-title="label"
              item-value="id"
              label="Model"
              variant="outlined"
              density="compact"
              readonly
              @update:model-value="handleEventModelChange"
            />
            <v-text-field v-model="form.start_date" type="date" label="Start date" variant="outlined" density="compact" />
            <v-text-field v-model="form.end_date" type="date" label="End date (optional)" variant="outlined" density="compact" hint="Leave blank for a one-day event" persistent-hint />
            <v-select
              v-model="form.studio_id"
              class="form-grid-full"
              :items="studioOptions"
              item-title="name"
              item-value="id"
              label="Visible for"
              variant="outlined"
              density="compact"
            />
          </div>
          <v-textarea v-model="form.notes" label="Notes (optional)" variant="outlined" density="compact" rows="3" />
          <v-alert v-if="dialogError" type="error" variant="tonal" density="compact">{{ dialogError }}</v-alert>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-btn v-if="editingEventId" color="error" variant="text" :loading="deleting" @click="deleteEvent">Delete</v-btn>
          <v-spacer />
          <v-btn variant="text" @click="eventDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="saving" @click="saveEvent">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="detailDialog" max-width="480">
      <v-card v-if="detailEvent">
        <v-card-title class="dialog-title">{{ detailEvent.title }}</v-card-title>
        <v-card-text class="pt-5">
          <div class="detail-row"><strong>Date</strong><span>{{ formatEventDates(detailEvent) }}</span></div>
          <div v-if="detailEvent.studio" class="detail-row"><strong>Studio</strong><span>{{ detailEvent.studio }}</span></div>
          <div v-if="detailEvent.website" class="detail-row"><strong>Site</strong><span>{{ detailEvent.website }}</span></div>
          <p v-if="detailEvent.notes" class="detail-notes">{{ detailEvent.notes }}</p>
          <div class="automatic-note">
            {{ isTaskEvent(detailEvent) ? 'This task is view only.' : 'This date is generated automatically and cannot be edited here.' }}
          </div>
        </v-card-text>
        <v-card-actions class="px-6 pb-5"><v-spacer /><v-btn color="primary" @click="detailDialog = false">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="taskDialog" max-width="560">
      <v-card v-if="taskForm">
        <v-card-title class="dialog-title task-dialog-heading">
          <span>{{ taskForm.id ? (taskForm.completed ? 'Completed Task' : 'Edit Task') : 'Add Task' }}</span>
          <span v-if="taskAssigneeName" class="task-dialog-model">{{ taskAssigneeName }}</span>
        </v-card-title>
        <v-card-text class="pt-5">
          <v-text-field v-model="taskForm.title" label="Title" variant="outlined" density="compact" autofocus />
          <v-textarea v-model="taskForm.description" label="Notes (optional)" variant="outlined" density="compact" rows="3" />
          <div class="form-grid">
            <v-autocomplete
              v-if="!taskForm.id && taskForm.assigned_shift !== 'Admin'"
              v-model="taskForm.model_id"
              :items="taskModelOptions"
              item-title="label"
              item-value="id"
              label="Model"
              variant="outlined"
              density="compact"
              clearable
              @update:model-value="handleTaskModelChange"
            />
            <v-text-field v-else-if="taskForm.model_id" :model-value="taskForm.model_name" label="Model" variant="outlined" density="compact" readonly />
            <v-autocomplete
              v-if="taskForm.assigned_shift === 'Admin'"
              v-model="taskForm.assigned_user_id"
              :items="adminChipOptions"
              item-title="label"
              item-value="id"
              label="Admin"
              variant="outlined"
              density="compact"
            />
            <v-select v-model="taskForm.assigned_shift" :items="availableTaskShiftOptions" label="Shift" variant="outlined" density="compact" @update:model-value="autofillTaskMonitor" />
            <v-select
              v-if="taskForm.assigned_shift !== 'Admin'"
              v-model="taskForm.monitor_id"
              :items="taskMonitorOptions"
              item-title="label"
              item-value="id"
              label="Monitor"
              variant="outlined"
              density="compact"
              clearable
            />
            <v-text-field v-model="taskForm.due_date" type="date" label="Due date (optional)" variant="outlined" density="compact" />
            <v-text-field :model-value="taskForm.studio" label="Studio" variant="outlined" density="compact" readonly />
          </div>
          <v-alert v-if="taskDialogError" type="error" variant="tonal" density="compact">{{ taskDialogError }}</v-alert>
        </v-card-text>
        <v-card-actions class="px-6 pb-5">
          <v-btn
            v-if="taskForm.id"
            :prepend-icon="taskForm.completed ? 'mdi-checkbox-blank-circle-outline' : 'mdi-check-circle-outline'"
            variant="text"
            :color="taskForm.completed ? 'grey-darken-1' : 'success'"
            :loading="taskSaving"
            @click="toggleCalendarTask"
          >{{ taskForm.completed ? 'Mark Pending' : 'Mark Completed' }}</v-btn>
          <v-spacer />
          <v-btn variant="text" @click="taskDialog = false">Cancel</v-btn>
          <v-btn color="primary" :loading="taskSaving" @click="saveCalendarTask">{{ taskForm.id ? 'Save Changes' : 'Save Task' }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import secureAxios from '@/utils/secureAxios';
import { getSiteColor } from '@/constants/siteColors';
import VueCal from 'vue-cal';
import 'vue-cal/dist/vuecal.css';

const blankForm = () => ({ title: '', event_type: 'event', website_id: null, studio_id: null, model_id: null, start_date: '', end_date: '', notes: '' });
const parseDateOnly = (value) => new Date(`${String(value).slice(0, 10)}T00:00:00Z`);
const formatDateOnly = (value) => value.toISOString().slice(0, 10);
const eachDate = (start, end) => {
  const dates = [];
  const cursor = parseDateOnly(start);
  const last = parseDateOnly(end);
  while (cursor <= last) {
    dates.push(formatDateOnly(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
};
const calendarWeekKey = (dateText) => {
  const date = parseDateOnly(dateText);
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() - day + 1);
  return formatDateOnly(date);
};

export default {
  name: 'OperationsCalendar',
  components: { VueCal },
  props: {
    embedded: { type: Boolean, default: false },
    focusedModelId: { type: [Number, String], default: null },
    focusedModelType: { type: String, default: 'studio' },
    focusedStudioId: { type: [Number, String], default: null },
  },
  data: () => ({
    loading: false,
    saving: false,
    deleting: false,
    errorMessage: '',
    dialogError: '',
    studios: [],
    sites: [],
    models: [],
    monitors: [],
    adminAssignees: [],
    events: [],
    authenticatedUser: JSON.parse(localStorage.getItem('user') || '{}'),
    eventTypes: [
      { name: 'Event', value: 'event' },
      { name: 'Marathon', value: 'marathon' },
    ],
    modelTypeFilter: null,
    studioFilter: null,
    contentFilters: {
      events: true,
      payments: true,
      birthdays: true,
      tasks: false,
    },
    calendarMode: 'calendar',
    modelPreviewSelection: 'all',
    taskAudienceFilter: 'all',
    monitorPreviewSelection: 'all',
    adminPreviewSelection: 'all',
    selectedDate: null,
    displayedYear: new Date().getFullYear(),
    eventDialog: false,
    detailDialog: false,
    editingEventId: null,
    detailEvent: null,
    taskDialog: false,
    taskSaving: false,
    taskDialogError: '',
    taskForm: null,
    taskShiftOptions: ['Morning', 'Evening', 'Night', 'Satellite'],
    form: blankForm(),
  }),
  computed: {
    isMasterCalendarAccount() {
      const email = String(this.authenticatedUser?.email || '').trim().toLowerCase();
      return ['carolina.r@mgrouplcs.com', 'jono@mgrouplcs.com', 'jorge.n@mgrouplcs.com'].includes(email);
    },
    calendarDefaultsToAllStudios() {
      return this.authenticatedUser?.role === 'support' || this.isMasterCalendarAccount;
    },
    canUseAdminTasks() {
      return this.authenticatedUser?.role === 'admin' || this.authenticatedUser?.isAdmin === true;
    },
    isModelAccount() {
      return this.authenticatedUser?.role === 'model';
    },
    availableTaskShiftOptions() {
      return this.canUseAdminTasks ? ['Admin', ...this.taskShiftOptions] : this.taskShiftOptions;
    },
    effectiveScopeStudioFilter() {
      if (this.studioFilter) return this.studioFilter;
      if (this.calendarDefaultsToAllStudios) return 'all';
      const ownStudioId = Number(this.authenticatedUser?.studio_id);
      return Number.isInteger(ownStudioId) && ownStudioId > 0 ? String(ownStudioId) : 'all';
    },
    studioOptions() {
      return [{ id: null, name: 'All studios' }, ...this.studios];
    },
    siteDropdownItems() {
      const mainOrder = ['flirt4free', 'streamate', 'livejasmin', 'chaturbate', 'stripchat'];
      const normalized = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
      const mainSites = mainOrder
        .map(key => this.sites.find(site => normalized(site.name) === key))
        .filter(Boolean);
      const mainIds = new Set(mainSites.map(site => Number(site.id)));
      const additionalSites = this.sites
        .filter(site => !mainIds.has(Number(site.id)))
        .sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));
      if (!mainSites.length || !additionalSites.length) return [...mainSites, ...additionalSites];
      return [
        ...mainSites,
        { id: '__site-divider__', name: '', isDivider: true, props: { disabled: true } },
        ...additionalSites,
      ];
    },
    selectedScopeStudioId() {
      return this.effectiveScopeStudioFilter === 'all' ? null : Number(this.effectiveScopeStudioFilter);
    },
    isModelEventContext() {
      return this.calendarMode === 'models' && this.modelPreviewSelection !== 'all';
    },
    showModelTray() {
      return !this.calendarDefaultsToAllStudios || Boolean(this.modelTypeFilter || this.studioFilter);
    },
    modelChipOptions() {
      if (this.isMasterCalendarAccount && !this.modelTypeFilter && !this.studioFilter) return [];
      return this.models
        .map(model => ({
          id: Number(model.id),
          studio_id: Number(model.studio_id),
          shift: String(model.shift || ''),
          label: String(model.stageName || model.fullName || '').trim(),
        }))
        .filter(model => model.id && model.label)
        .sort((left, right) => left.label.localeCompare(right.label));
    },
    taskModelOptions() {
      return this.models
        .map(model => ({
          id: Number(model.id),
          studio_id: Number(model.studio_id),
          shift: String(model.shift || ''),
          label: String(model.stageName || model.fullName || '').trim(),
          fullName: String(model.fullName || '').trim(),
          studio: String(model.studio || '').trim(),
        }))
        .filter(model => model.id && model.label)
        .sort((left, right) => left.label.localeCompare(right.label));
    },
    taskMonitorOptions() {
      if (!this.taskForm) return [];
      return this.monitors
        .filter(monitor => (
          (!this.taskForm.studio_id || Number(monitor.studio_id) === Number(this.taskForm.studio_id))
          && (!this.taskForm.assigned_shift || monitor.shift === this.taskForm.assigned_shift)
        ))
        .map(monitor => ({ id: Number(monitor.id), label: monitor.displayName || monitor.fullName }))
        .sort((left, right) => String(left.label).localeCompare(String(right.label)));
    },
    monitorChipOptions() {
      return this.monitors
        .map(monitor => ({
          id: Number(monitor.id),
          studio_id: Number(monitor.studio_id),
          shift: String(monitor.shift || ''),
          label: String(monitor.displayName || monitor.fullName || '').trim(),
        }))
        .filter(monitor => monitor.id && monitor.label)
        .sort((left, right) => left.label.localeCompare(right.label));
    },
    adminChipOptions() {
      return this.adminAssignees
        .map(admin => ({
          id: Number(admin.id),
          studio_id: Number(admin.studio_id),
          email: String(admin.email || '').trim(),
          label: this.adminDisplayName(admin.email),
        }))
        .filter(admin => admin.id && admin.label)
        .sort((left, right) => left.label.localeCompare(right.label));
    },
    taskAssigneeName() {
      if (!this.taskForm) return '';
      if (this.taskForm.model_id) return this.taskForm.model_name || '';
      if (this.taskForm.assigned_user_id) {
        const admin = this.adminChipOptions.find(item => item.id === Number(this.taskForm.assigned_user_id));
        return admin?.label || this.adminDisplayName(this.taskForm.assigned_user_email);
      }
      const monitor = this.monitors.find(item => Number(item.id) === Number(this.taskForm.monitor_id));
      return monitor?.displayName || monitor?.fullName || this.taskForm.monitor_name || '';
    },
    calendarEvents() {
      let modeEvents;
      if (this.isModelAccount && this.embedded) {
        modeEvents = this.events.filter(event => (
          !this.isTaskEvent(event)
          || (event.source === 'model-task' && Number(event.model_id) === Number(this.focusedModelId))
        ));
      } else if (this.contentFilters.tasks) {
        modeEvents = this.events.filter(event => {
          if (!this.isTaskEvent(event)) return false;
          if (this.taskAudienceFilter === 'models') {
            return event.source === 'model-task'
              && (this.modelPreviewSelection === 'all' || Number(event.model_id) === Number(this.modelPreviewSelection));
          }
          if (this.taskAudienceFilter === 'monitors') {
            return event.source === 'monitor-task'
              && (this.monitorPreviewSelection === 'all' || Number(event.monitor_id) === Number(this.monitorPreviewSelection));
          }
          if (this.taskAudienceFilter === 'admin') {
            return event.source === 'admin-task'
              && (this.adminPreviewSelection === 'all' || Number(event.assigned_user_id) === Number(this.adminPreviewSelection));
          }
          return true;
        });
      } else {
        modeEvents = this.calendarMode === 'calendar'
          ? this.events
          : this.events.filter(event => (
            event.model_id
            && (this.modelPreviewSelection === 'all' || Number(event.model_id) === Number(this.modelPreviewSelection))
          ));
      }
      const sourceEnabled = (source) => {
        if (source === 'competition') return this.contentFilters.events;
        if (source === 'payment-main' || source === 'payment-cb') return this.contentFilters.payments;
        if (source === 'birthday-model' || source === 'birthday-monitor') return this.contentFilters.birthdays;
        if (source === 'model-task' || source === 'monitor-task' || source === 'admin-task') return this.contentFilters.tasks;
        return true;
      };
      const scopedEvents = modeEvents.filter(event => sourceEnabled(event.source));
      const visibleEvents = scopedEvents.map((event) => ({
        ...event,
        start: `${event.start_date} 00:00`,
        end: `${event.end_date} 23:59`,
        allDay: true,
        class: this.isTaskEvent(event)
          ? `event-model event-model-${this.taskVisualState(event)}`
          : `event-${event.source}${event.source === 'competition' && event.website && event.event_type !== 'marathon' ? ' event-site-colored' : ''}${event.event_type === 'marathon' ? ' event-type-marathon' : ''}`,
      }));

      // Vue Cal renders each day of a multi-day event separately. Reserve the
      // same number of rows above competitions throughout each calendar week,
      // so payments and birthdays remain above without breaking the visual bar.
      const eventsAboveRanges = visibleEvents.filter((event) => (
        event.source !== 'competition' || event.start_date === event.end_date
      ));
      const competitionDatesByWeek = new Map();
      visibleEvents
        .filter((event) => event.source === 'competition' && event.start_date !== event.end_date)
        .forEach((event) => {
          eachDate(event.start_date, event.end_date).forEach((date) => {
            const week = calendarWeekKey(date);
            if (!competitionDatesByWeek.has(week)) competitionDatesByWeek.set(week, new Set());
            competitionDatesByWeek.get(week).add(date);
          });
        });

      const spacers = [];
      competitionDatesByWeek.forEach((dateSet, week) => {
        const dates = [...dateSet];
        const eventCount = (date) => eventsAboveRanges.filter((event) => (
          event.start_date <= date && event.end_date >= date
        )).length;
        const reservedRows = Math.max(0, ...dates.map(eventCount));
        dates.forEach((date) => {
          const missingRows = reservedRows - eventCount(date);
          for (let index = 0; index < missingRows; index += 1) {
            spacers.push({
              id: `calendar-spacer-${week}-${date}-${index}`,
              title: '',
              start_date: date,
              end_date: date,
              start: `${date} 00:00`,
              end: `${date} 23:59`,
              allDay: true,
              source: 'spacer',
              class: 'event-spacer',
              editable: false,
            });
          }
        });
      });

      const eventPriority = (event) => {
        if (event.source === 'spacer') return 1;
        if (event.source === 'competition' && event.start_date !== event.end_date) return 2;
        return 0;
      };
      return [...visibleEvents, ...spacers].sort((left, right) => (
        eventPriority(left) - eventPriority(right)
        || String(left.start_date).localeCompare(String(right.start_date))
        || String(left.id).localeCompare(String(right.id))
      ));
    },
  },
  mounted() {
    this.initializeCalendar();
  },
  methods: {
    async initializeCalendar() {
      const modelId = Number(this.embedded ? this.focusedModelId : this.$route?.query?.model);
      const requestedScopeType = this.embedded ? this.focusedModelType : this.$route?.query?.type;
      const scopeType = requestedScopeType === 'sat' ? 'sat' : 'studio';
      const studioId = Number(this.embedded ? this.focusedStudioId : this.$route?.query?.studio);
      if (Number.isInteger(modelId) && modelId > 0) {
        this.calendarMode = 'models';
        this.taskAudienceFilter = 'models';
        this.modelPreviewSelection = modelId;
        if (this.isModelAccount && this.embedded) {
          this.contentFilters = { events: true, payments: true, birthdays: true, tasks: true };
        } else {
          this.showTasksOnly();
        }
        this.modelTypeFilter = scopeType;
        if (Number.isInteger(studioId) && studioId > 0) this.studioFilter = String(studioId);
      }
      await this.loadCalendar();
    },
    async loadCalendar() {
      this.loading = true;
      this.errorMessage = '';
      try {
        const { data } = await secureAxios.get('/api/operations-calendar', {
          params: {
            year: this.displayedYear,
            scope: `${this.modelTypeFilter || 'both'}-${this.effectiveScopeStudioFilter}`,
          },
        });
        this.studios = data.studios || [];
        this.sites = data.sites || [];
        this.models = data.models || [];
        this.monitors = data.monitors || [];
        this.adminAssignees = data.adminAssignees || [];
        this.events = data.events || [];
        if (this.modelPreviewSelection !== 'all' && !this.modelChipOptions.some(model => model.id === Number(this.modelPreviewSelection))) {
          this.modelPreviewSelection = 'all';
        }
        if (this.adminPreviewSelection !== 'all' && !this.adminChipOptions.some(admin => admin.id === Number(this.adminPreviewSelection))) {
          this.adminPreviewSelection = 'all';
        }
      } catch (error) {
        this.errorMessage = error.response?.data?.message || 'Unable to load the calendar.';
      } finally {
        this.loading = false;
      }
    },
    handleViewChange(view) {
      const start = view?.startDate || view?.start;
      const year = start ? new Date(start).getFullYear() : null;
      if (year && year !== this.displayedYear) {
        this.displayedYear = year;
        this.loadCalendar();
      }
    },
    async showStandardCalendar() {
      this.calendarMode = 'calendar';
      this.modelPreviewSelection = 'all';
      this.modelTypeFilter = null;
      this.studioFilter = null;
      this.contentFilters.events = true;
      this.contentFilters.payments = true;
      this.contentFilters.birthdays = true;
      this.contentFilters.tasks = false;
      this.taskAudienceFilter = 'all';
      this.monitorPreviewSelection = 'all';
      this.adminPreviewSelection = 'all';
      await this.loadCalendar();
    },
    showTasksCalendar() {
      this.calendarMode = 'calendar';
      this.taskAudienceFilter = 'all';
      this.modelPreviewSelection = 'all';
      this.monitorPreviewSelection = 'all';
      this.adminPreviewSelection = 'all';
      this.showTasksOnly();
    },
    selectTaskAudience(audience) {
      this.taskAudienceFilter = audience;
      this.calendarMode = audience === 'models' ? 'models' : 'calendar';
      if (audience !== 'models') this.modelPreviewSelection = 'all';
      if (audience !== 'monitors') this.monitorPreviewSelection = 'all';
      if (audience !== 'admin') this.adminPreviewSelection = 'all';
    },
    handleModelTypeChange(value) {
      if (!value) {
        this.studioFilter = null;
        return;
      }

      if (!this.studioFilter) {
        const ownStudioId = Number(this.authenticatedUser?.studio_id) || null;
        const ownStudioExists = this.studios.some(studio => Number(studio.id) === ownStudioId);
        this.studioFilter = this.calendarDefaultsToAllStudios || !ownStudioExists ? 'all' : String(ownStudioId);
      }
      this.loadCalendar();
    },
    handleStudioChange(value) {
      if (!value) return;
      if (!this.modelTypeFilter) this.modelTypeFilter = 'both';
      this.loadCalendar();
    },
    async selectModelCalendar(modelSelection) {
      this.calendarMode = 'models';
      this.taskAudienceFilter = 'models';
      this.showTasksOnly();
      if (modelSelection === 'all') {
        this.modelPreviewSelection = 'all';
        return;
      }

      const model = modelSelection || {};
      const modelId = Number(model.id);
      const studioId = Number(model.studio_id);
      this.modelPreviewSelection = modelId;
      this.modelTypeFilter = this.modelTypeFilter || 'studio';
      if (Number.isInteger(studioId) && studioId > 0) {
        this.studioFilter = String(studioId);
      }
      await this.loadCalendar();
    },
    async selectMonitorCalendar(monitorSelection) {
      this.taskAudienceFilter = 'monitors';
      this.showTasksOnly();
      if (monitorSelection === 'all') {
        this.monitorPreviewSelection = 'all';
        return;
      }
      const monitor = monitorSelection || {};
      this.monitorPreviewSelection = Number(monitor.id);
      const studioId = Number(monitor.studio_id);
      if (Number.isInteger(studioId) && studioId > 0) this.studioFilter = String(studioId);
      if (monitor.shift === 'Satellite') this.modelTypeFilter = 'sat';
      else this.modelTypeFilter = 'studio';
      await this.loadCalendar();
    },
    async selectAdminCalendar(adminSelection) {
      this.taskAudienceFilter = 'admin';
      this.calendarMode = 'calendar';
      this.showTasksOnly();
      if (adminSelection === 'all') {
        this.adminPreviewSelection = 'all';
        return;
      }
      const admin = adminSelection || {};
      this.adminPreviewSelection = Number(admin.id);
      const studioId = Number(admin.studio_id);
      if (Number.isInteger(studioId) && studioId > 0) this.studioFilter = String(studioId);
      this.modelTypeFilter = 'studio';
      await this.loadCalendar();
    },
    openCreateDialog(eventType = 'event') {
      const today = new Date().toISOString().slice(0, 10);
      const selectedCalendarDate = this.selectedDate
        ? formatDateOnly(new Date(this.selectedDate))
        : today;
      const selectedModel = this.modelPreviewSelection === 'all'
        ? null
        : this.modelChipOptions.find(model => model.id === Number(this.modelPreviewSelection));
      this.editingEventId = null;
      this.form = {
        ...blankForm(),
        title: '',
        event_type: eventType,
        model_id: selectedModel?.id || null,
        start_date: selectedCalendarDate,
        end_date: '',
        studio_id: selectedModel?.studio_id || this.selectedScopeStudioId,
      };
      this.dialogError = '';
      this.eventDialog = true;
    },
    openCreateTaskDialog() {
      const today = new Date().toISOString().slice(0, 10);
      const dueDate = this.selectedDate ? formatDateOnly(new Date(this.selectedDate)) : today;
      const selectedModel = this.modelPreviewSelection === 'all'
        ? null
        : this.taskModelOptions.find(model => model.id === Number(this.modelPreviewSelection));
      const selectedMonitor = this.monitorPreviewSelection === 'all'
        ? null
        : this.monitorChipOptions.find(monitor => monitor.id === Number(this.monitorPreviewSelection));
      const selectedAdmin = this.adminPreviewSelection === 'all'
        ? null
        : this.adminChipOptions.find(admin => admin.id === Number(this.adminPreviewSelection));
      this.taskForm = {
        id: null,
        title: '',
        description: '',
        model_id: selectedModel?.id || null,
        monitor_id: selectedMonitor?.id || null,
        assigned_user_id: selectedAdmin?.id || null,
        assigned_shift: selectedAdmin ? 'Admin' : selectedModel?.shift || selectedMonitor?.shift || (this.modelTypeFilter === 'sat' ? 'Satellite' : 'Morning'),
        due_date: dueDate,
        completed: false,
        model_name: selectedModel?.label || '',
        studio: selectedModel?.studio || '',
        studio_id: selectedModel?.studio_id || selectedMonitor?.studio_id || selectedAdmin?.studio_id || this.selectedScopeStudioId,
      };
      if (!selectedMonitor) this.autofillTaskMonitor();
      this.taskDialogError = '';
      this.taskDialog = true;
    },
    handleTaskModelChange(modelId) {
      const model = this.taskModelOptions.find(item => item.id === Number(modelId));
      this.taskForm.model_name = model?.label || '';
      this.taskForm.studio = model?.studio || '';
      this.taskForm.studio_id = model?.studio_id || null;
      if (model?.shift) this.taskForm.assigned_shift = model.shift;
      this.autofillTaskMonitor();
    },
    autofillTaskMonitor() {
      if (!this.taskForm) return;
      if (this.taskForm.assigned_shift === 'Admin') {
        this.taskForm.model_id = null;
        this.taskForm.monitor_id = null;
        this.taskForm.assigned_user_id = this.taskForm.assigned_user_id || Number(this.authenticatedUser?.id) || null;
        return;
      }
      this.taskForm.assigned_user_id = null;
      if (this.taskForm.assigned_shift === 'Satellite') {
        if (this.taskForm) this.taskForm.monitor_id = null;
        return;
      }
      const monitor = this.monitors.find(item => (
        Number(item.studio_id) === Number(this.taskForm.studio_id)
        && item.shift === this.taskForm.assigned_shift
      ));
      this.taskForm.monitor_id = monitor?.id || null;
    },
    handleCellClick(date) {
      if (!date) return;
      this.selectedDate = new Date(date);
    },
    isTaskEvent(event) {
      return event?.source === 'model-task' || event?.source === 'monitor-task' || event?.source === 'admin-task';
    },
    adminDisplayName(email) {
      return String(email || '')
        .split('@')[0]
        .split(/[._-]+/)
        .filter(Boolean)
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    },
    openEvent(event) {
      if (this.isModelAccount && this.isTaskEvent(event)) {
        this.detailEvent = event;
        this.detailDialog = true;
        return;
      }
      if (this.isTaskEvent(event)) {
        this.taskForm = {
          id: Number(event.task_id),
          title: event.title || '',
          description: event.description || event.notes || '',
          model_id: Number(event.model_id) || null,
          monitor_id: Number(event.monitor_id) || null,
          assigned_user_id: Number(event.assigned_user_id) || null,
          assigned_shift: event.assigned_shift || 'Morning',
          due_date: event.start_date || '',
          completed: Boolean(event.completed),
          model_name: event.model_name || event.model_stage_name || event.model_full_name || '',
          monitor_name: event.monitor_name || event.monitor_display_name || event.monitor_full_name || '',
          assigned_user_email: event.assigned_user_email || '',
          studio: event.studio || '',
          studio_id: Number(event.studio_id) || null,
        };
        this.taskDialogError = '';
        this.taskDialog = true;
        return;
      }
      if (event.editable) {
        this.editingEventId = event.id;
        this.form = {
          title: event.title,
          event_type: event.event_type || 'event',
          website_id: event.website_id || null,
          studio_id: event.studio_id || null,
          model_id: event.model_id || null,
          start_date: event.start_date,
          end_date: event.end_date,
          notes: event.notes || '',
        };
        this.dialogError = '';
        this.eventDialog = true;
      } else {
        this.detailEvent = event;
        this.detailDialog = true;
      }
    },
    async saveCalendarTask() {
      if (!this.taskForm?.title?.trim()) {
        this.taskDialogError = 'Title is required.';
        return;
      }
      if (!this.taskForm.model_id && !this.taskForm.monitor_id && !this.taskForm.assigned_user_id) {
        this.taskDialogError = 'A model, monitor, or admin is required.';
        return;
      }
      this.taskSaving = true;
      this.taskDialogError = '';
      try {
        const payload = {
          title: this.taskForm.title,
          description: this.taskForm.description,
          model_id: this.taskForm.model_id,
          monitor_id: this.taskForm.monitor_id,
          assigned_user_id: this.taskForm.assigned_user_id,
          assigned_shift: this.taskForm.assigned_shift,
          due_date: this.taskForm.due_date,
        };
        if (this.taskForm.id) await secureAxios.put(`/api/operational-tasks/${this.taskForm.id}`, payload);
        else await secureAxios.post('/api/operational-tasks', payload);
        this.taskDialog = false;
        await this.loadCalendar();
      } catch (error) {
        this.taskDialogError = error.response?.data?.message || 'Unable to save the task.';
      } finally {
        this.taskSaving = false;
      }
    },
    async toggleCalendarTask() {
      if (!this.taskForm) return;
      this.taskSaving = true;
      this.taskDialogError = '';
      try {
        const completed = !this.taskForm.completed;
        await secureAxios.put(`/api/operational-tasks/${this.taskForm.id}/complete`, { completed });
        this.taskForm.completed = completed;
        await this.loadCalendar();
      } catch (error) {
        this.taskDialogError = error.response?.data?.message || 'Unable to update the task.';
      } finally {
        this.taskSaving = false;
      }
    },
    toggleContentFilter(filter) {
      if (filter === 'tasks') {
        if (this.contentFilters.tasks) {
          this.contentFilters.events = true;
          this.contentFilters.payments = true;
          this.contentFilters.birthdays = true;
          this.contentFilters.tasks = false;
        } else {
          this.showTasksOnly();
        }
        return;
      }

      if (this.contentFilters.tasks) {
        this.contentFilters.events = false;
        this.contentFilters.payments = false;
        this.contentFilters.birthdays = false;
        this.contentFilters.tasks = false;
        this.contentFilters[filter] = true;
        return;
      }
      this.contentFilters[filter] = !this.contentFilters[filter];
    },
    showTasksOnly() {
      this.contentFilters.events = false;
      this.contentFilters.payments = false;
      this.contentFilters.birthdays = false;
      this.contentFilters.tasks = true;
    },
    taskVisualState(event) {
      if (event.completed) return 'completed';
      const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Bogota' }).format(new Date());
      if (event.start_date < today) return 'overdue';
      if (event.start_date === today) return 'today';
      return 'upcoming';
    },
    eventContentStyle(event) {
      if (event.source !== 'competition' || !event.website || event.event_type === 'marathon') return null;
      const color = getSiteColor(event.website);
      return {
        backgroundColor: `${color}24`,
        boxShadow: `inset 0 0 0 1px ${color}`,
      };
    },
    handleEventModelChange(modelId) {
      const model = this.modelChipOptions.find(item => item.id === Number(modelId));
      if (!model) return;
      this.form.studio_id = model.studio_id;
    },
    async saveEvent() {
      if (!this.form.title || !this.form.start_date) {
        this.dialogError = 'Title and start date are required.';
        return;
      }
      if (this.isModelEventContext && !this.form.model_id) {
        this.dialogError = 'A model is required for this event.';
        return;
      }
      this.saving = true;
      this.dialogError = '';
      try {
        const payload = {
          ...this.form,
          end_date: this.form.end_date || this.form.start_date,
        };
        if (!this.editingEventId && this.form.model_id) {
          const selectedModel = this.modelChipOptions.find(model => model.id === Number(this.form.model_id));
          await secureAxios.post('/api/operational-tasks', {
            title: this.form.title,
            description: this.form.notes,
            model_id: this.form.model_id,
            assigned_shift: selectedModel?.shift || (this.modelTypeFilter === 'sat' ? 'Satellite' : 'Morning'),
            due_date: this.form.start_date,
          });
        } else if (this.editingEventId) await secureAxios.put(`/api/operations-calendar/${this.editingEventId}`, payload);
        else await secureAxios.post('/api/operations-calendar', payload);
        this.eventDialog = false;
        await this.loadCalendar();
      } catch (error) {
        this.dialogError = error.response?.data?.message || 'Unable to save the event.';
      } finally {
        this.saving = false;
      }
    },
    async deleteEvent() {
      if (!window.confirm('Delete this event from the calendar?')) return;
      this.deleting = true;
      this.dialogError = '';
      try {
        await secureAxios.delete(`/api/operations-calendar/${this.editingEventId}`);
        this.eventDialog = false;
        await this.loadCalendar();
      } catch (error) {
        this.dialogError = error.response?.data?.message || 'Unable to delete the event.';
      } finally {
        this.deleting = false;
      }
    },
    formatEventDates(event) {
      return event.start_date === event.end_date ? event.start_date : `${event.start_date} – ${event.end_date}`;
    },
  },
};
</script>

<style scoped>
.calendar-page { max-width: 1500px; }
.calendar-page--embedded { padding: 0 !important; }
.calendar-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; margin-bottom: 16px; }
.section-kicker { color: #1976a8; font-size: 11px; font-weight: 800; letter-spacing: .12em; }
.calendar-heading h1 { margin: 2px 0 0; color: #172b40; font-size: 28px; line-height: 1.15; }
.calendar-heading p { margin: 5px 0 0; color: #607386; font-size: 13px; }
.calendar-add-group { height: 34px; }
:deep(.calendar-add-group .v-btn) { min-height: 34px; height: 34px; }
:deep(.calendar-add-group .v-btn:last-child) { min-width: 34px; width: 34px; padding: 0; }
.calendar-card { overflow: hidden; border: 1px solid #d8e1e8; border-top: 4px solid #347ba7; }
.embedded-calendar-toolbar { display: flex; min-height: 44px; align-items: center; justify-content: space-between; gap: 16px; padding: 8px 14px; border-bottom: 1px solid #dbe4eb; background: #f8fafc; }
.embedded-calendar-toolbar > div:first-child { display: flex; align-items: center; gap: 7px; color: #263f55; }
.embedded-calendar-toolbar strong { font-size: 13px; letter-spacing: .02em; text-transform: uppercase; }
.calendar-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 12px 14px; border-bottom: 1px solid #dbe4eb; }
.calendar-scope-controls { display: flex; min-width: 0; align-items: center; gap: 8px; }
.calendar-view-toggle { display: inline-flex; flex: 0 0 auto; }
.calendar-mode-button { display: inline-flex; min-height: 32px; flex: 0 0 auto; align-items: center; gap: 6px; padding: 5px 11px; border: 1px solid #c8d4de; border-radius: 4px; background: #fff; color: #50677b; font-size: 11px; font-weight: 800; line-height: 1; cursor: pointer; }
.calendar-view-toggle .calendar-mode-button + .calendar-mode-button { margin-left: -1px; }
.calendar-view-toggle .calendar-mode-button:first-child { border-top-right-radius: 0; border-bottom-right-radius: 0; }
.calendar-view-toggle .calendar-mode-button:last-child { border-top-left-radius: 0; border-bottom-left-radius: 0; }
.calendar-mode-button:hover { border-color: #7fa9c7; background: #f0f7fb; color: #214d6d; }
.calendar-mode-button.is-selected { position: relative; z-index: 1; border-color: #7fa9c7; background: #e7f1f8; color: #214d6d; box-shadow: 0 1px 3px rgba(47, 125, 185, .12); }
.calendar-mode-button.is-selected :deep(.v-icon) { color: #2f7db9; }
.calendar-scope-group { display: flex; min-height: 32px; align-items: stretch; gap: 0; overflow: hidden; border: 1px solid #c8d4de; border-radius: 4px; background: #fff; }
.calendar-scope-group > span { display: inline-flex; align-items: center; padding: 0 8px; border-right: 1px solid #c8d4de; background: #f5f8fa; color: #607487; font-size: 9px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; white-space: nowrap; }
:deep(.calendar-scope-group .v-btn-toggle) { height: 30px; border: 0; border-radius: 0; }
:deep(.calendar-scope-group .v-btn) { min-height: 30px; border: 0; border-radius: 0; text-transform: none; }
:deep(.calendar-scope-group .v-btn + .v-btn) { border-left: 1px solid #d8e1e8; }
:deep(.calendar-scope-group .calendar-scope-icon-button) { min-width: 36px; padding-right: 7px; padding-left: 7px; }
:deep(.calendar-scope-group .v-btn--active),
:deep(.calendar-scope-group .v-btn--selected) { background: #2f7db9 !important; color: #fff !important; box-shadow: 0 1px 3px rgba(47, 125, 185, .22); }
:deep(.calendar-scope-group .v-btn--active .v-btn__overlay),
:deep(.calendar-scope-group .v-btn--selected .v-btn__overlay) { opacity: 0 !important; }
.calendar-legend { display: flex; align-items: center; flex-wrap: wrap; justify-content: flex-end; gap: 6px; color: #52677a; }
.calendar-content-filter { display: inline-flex; min-height: 29px; align-items: center; gap: 6px; padding: 4px 9px; border: 1px solid #d3dde5; border-radius: 999px; background: #fff; color: #718394; font-size: 11px; font-weight: 700; cursor: pointer; opacity: .62; transition: background .15s, border-color .15s, color .15s, opacity .15s, box-shadow .15s; }
.calendar-content-filter:hover { border-color: #9bb7ca; background: #f5f9fc; color: #344f65; opacity: 1; }
.calendar-content-filter.is-selected { border-color: #b9cbd8; background: #f3f7fa; color: #344f65; box-shadow: 0 1px 2px rgba(32, 55, 73, .08); opacity: 1; }
.legend-double-dot { display: inline-flex; align-items: center; gap: 2px; }
.task-status-legend span { display: inline-flex; align-items: center; gap: 6px; color: #52677a; font-size: 11px; font-weight: 700; white-space: nowrap; }
.legend-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }
.legend-dot.competition { background: #c8e5d3; border: 1px solid #72ad88; }
.legend-dot.payment-main { background: #1976d2; border: 1px solid #1976d2; }
.legend-dot.payment-cb { background: #f59e0b; border: 1px solid #f59e0b; }
.legend-dot.birthday { background: #e8afc3; border: 1px solid #bd6485; }
.legend-dot.task-upcoming { background: #eaf3fb; border: 1px solid #7fafd1; }
.legend-dot.task-today { background: #fff2cc; border: 1px solid #d9a928; }
.legend-dot.task-overdue { background: #fbe7e8; border: 1px solid #d4777d; }
.legend-dot.task-completed { background: #f1f3f5; border: 1px solid #bec6cc; }
.calendar-model-strip { display: flex; min-width: 0; align-items: center; gap: 10px; padding: 8px 14px; border-bottom: 1px solid #dbe4eb; background: #f8fafc; }
.calendar-task-audience-strip { display: flex; min-width: 0; align-items: center; gap: 10px; padding: 7px 14px; border-bottom: 1px solid #dbe4eb; background: #f8fafc; }
.calendar-task-audience-strip .calendar-view-toggle { flex-wrap: wrap; }
.calendar-model-label { flex: 0 0 auto; color: #607487; font-size: 10px; font-weight: 800; letter-spacing: .08em; text-transform: uppercase; }
.calendar-model-pills { display: flex; min-width: 0; flex: 1 1 auto; flex-wrap: wrap; align-items: center; gap: 6px; padding: 2px 1px; }
.calendar-model-pill { flex: 0 0 auto; min-height: 27px; padding: 4px 11px; border: 1px solid #ccd7e0; border-radius: 999px; background: #fff; color: #50677b; font-size: 11px; font-weight: 700; line-height: 1; cursor: pointer; transition: background .15s, border-color .15s, color .15s, box-shadow .15s; }
.calendar-model-pill:hover { border-color: #7fa9c7; background: #f0f7fb; color: #214d6d; }
.calendar-model-pill.is-selected { border-color: #2f7db9; background: #2f7db9; color: #fff; box-shadow: 0 1px 3px rgba(47, 125, 185, .22); }
.calendar-wrap { position: relative; min-height: 790px; transition: opacity .2s; }
.calendar-wrap.is-loading { opacity: .72; }
.calendar-loader { position: absolute; inset: 0 0 auto; z-index: 4; }
.modato-calendar { height: 790px; border: 0; }
.dialog-title { background: #263f55; color: white; font-size: 17px; font-weight: 800; }
.task-dialog-heading { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
.task-dialog-model { overflow: hidden; padding: 4px 9px; border: 1px solid rgba(255,255,255,.32); border-radius: 999px; font-size: 11px; font-weight: 800; text-overflow: ellipsis; white-space: nowrap; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0 12px; }
.form-grid-full { grid-column: 1 / -1; }
.site-select-divider { height: 4px; margin: 5px 9px; border-radius: 2px; background: #263f55; pointer-events: none; }
.detail-row { display: flex; justify-content: space-between; gap: 20px; padding: 8px 0; border-bottom: 1px solid #e3e9ee; color: #31465a; }
.detail-notes { margin: 16px 0 0; white-space: pre-wrap; color: #3e5367; }
.automatic-note { margin-top: 16px; padding: 10px 12px; border-radius: 6px; background: #eef5fa; color: #527087; font-size: 12px; }

:deep(.vuecal__title-bar) { min-height: 56px; padding: 0 16px; border-bottom: 1px solid #cfd9e2; background: #f2f6fa; color: #243d53; }
:deep(.vuecal__title) { font-size: 20px; font-weight: 800; }
:deep(.vuecal__arrow) { display: inline-flex; width: 32px; height: 32px; align-items: center; justify-content: center; margin: 0; border: 1px solid #cbd6df; border-radius: 4px; background: #fff; color: #4a6073; }
:deep(.vuecal__arrow:hover), :deep(.vuecal__today-btn:hover) { background: #e8eff5; color: #213a50; }
:deep(.vuecal__arrow--prev) { margin-right: 8px; }
:deep(.vuecal__today-btn) { display: inline-flex; margin-right: 8px; padding: 6px 12px; border: 1px solid #cbd6df; border-radius: 4px; background: #fff; color: #334b60; font-size: 12px; font-weight: 800; text-transform: none; }
:deep(.vuecal__weekdays-headings) { min-height: 42px; margin-bottom: 0; border-bottom: 1px solid #cfd9e2; background: #f8fafc; }
:deep(.weekday-label) { color: #526b80; font-size: 11px; font-weight: 800; letter-spacing: .05em; text-transform: uppercase; }
:deep(.vuecal__cell) { background: #fff; }
:deep(.vuecal__cell:hover) { background: #f7fafc; }
:deep(.vuecal__cell::before) { border-color: rgba(183, 197, 208, .58); }
:deep(.vuecal__cell--today) { background: #fff; }
:deep(.vuecal__cell--today:hover) { background: #f7fafc; }
:deep(.vuecal__cell--selected) { background: #eaf4fb; }
:deep(.vuecal__cell--selected:hover) { background: #e2f0f9; }
:deep(.vuecal__cell--today .vuecal__cell-date) {
  display: inline-flex;
  box-sizing: border-box;
  width: 26px;
  height: 26px;
  top: 4px;
  right: 5px;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #2f7db9;
  color: #fff;
  padding: 1px 0 0 1px;
  line-height: 1;
  text-align: center;
  box-shadow: 0 2px 5px rgba(47, 125, 185, .28);
}
:deep(.vuecal__cell-date) { position: absolute; top: 9px; right: 10px; z-index: 2; color: #344d62; font-size: 14px; font-weight: 800; line-height: 1; }
:deep(.vuecal--month-view .vuecal__cell-events) { box-sizing: border-box; padding-top: 30px; }
:deep(.vuecal__cell--out-of-scope) { background: #f7f9fb; }
:deep(.vuecal__cell--out-of-scope:hover) { background: #f1f5f8; }
:deep(.vuecal__cell--out-of-scope .vuecal__cell-date) { color: #c1ccd5; font-weight: 800; }
:deep(.vuecal__event) { box-sizing: border-box; width: calc(100% - 12px); height: 23px; min-height: 23px; margin: 2px 6px; padding: 0; border: 1px solid transparent; border-radius: 4px; color: #15212b; font-size: 13px; font-weight: 800; cursor: pointer; box-shadow: 0 1px 1px rgba(32, 55, 73, .05); }
.calendar-event-content { display: flex; box-sizing: border-box; width: 100%; height: 100%; min-width: 0; align-items: center; gap: 4px; padding: 1px 6px; border-radius: inherit; color: #17212b; line-height: 1.15; text-align: left; }
.calendar-event-icon { flex: 0 0 auto; opacity: .78; }
.calendar-event-title { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.calendar-birthday-icon { margin-left: auto; flex: 0 0 auto; color: #a64f70; opacity: .95; }
.calendar-payment-icon { margin-left: auto; flex: 0 0 auto; opacity: .76; }
:deep(.event-competition) { background: #e5f3ea; border-color: #acd3ba; color: #214c31; }
:deep(.event-competition.event-site-colored) { border-color: transparent; background: transparent; }
:deep(.event-competition.event-site-colored .calendar-event-content) { border-radius: 4px; }
:deep(.event-competition.event-type-marathon) { background: #ffe45c; border: 2px solid #f59e0b; color: #17212b; box-shadow: 0 1px 4px rgba(180, 112, 0, .34); }
:deep(.event-competition.event-type-marathon .calendar-event-content) { color: #17212b; font-weight: 900; }
:deep(.event-model), :deep(.event-model-upcoming) { background: #eaf3fb; border-color: #7fafd1; color: #234f6d; }
:deep(.event-model-today) { background: #fff2cc; border-color: #d9a928; color: #684b00; }
:deep(.event-model-overdue) { background: #fbe7e8; border-color: #d4777d; color: #7e292f; }
:deep(.event-model-completed) { background: #f1f3f5; border-color: #bec6cc; color: #737d85; box-shadow: none; }
:deep(.event-model-completed .calendar-event-title) { opacity: .58; }
.completed-task-icon { color: #7d8790; opacity: 1; }
:deep(.event-payment-main) { background: #eef6ff; border-color: #1976d2; color: #075985; }
:deep(.event-payment-cb) { background: #fff7e6; border-color: #f59e0b; color: #92400e; }
:deep(.event-payment-main .calendar-event-content) { color: #075985; }
:deep(.event-payment-cb .calendar-event-content) { color: #92400e; }
:deep(.event-birthday-model), :deep(.event-birthday-monitor) { background: #f9dfe8; border-color: #e8afc3; color: #42192b; }
:deep(.event-competition.vuecal__event--multiple-days) {
  width: calc(100% + 2px);
  margin: 2px -1px;
  border-right-width: 0;
  border-left-width: 0;
  border-radius: 0;
  box-shadow: none;
}
:deep(.event-competition.vuecal__event--multiple-days.event-start) {
  width: calc(100% - 5px);
  margin-right: -1px;
  margin-left: 6px;
  border-left-width: 1px;
  border-radius: 4px 0 0 4px;
}
:deep(.event-competition.vuecal__event--multiple-days.event-end) {
  width: calc(100% - 5px);
  margin-right: 6px;
  margin-left: -1px;
  border-right-width: 1px;
  border-radius: 0 4px 4px 0;
}
:deep(.event-competition.vuecal__event--multiple-days .calendar-event-content) {
  padding-right: 8px;
  padding-left: 8px;
}
:deep(.event-spacer) { height: 23px; min-height: 23px; visibility: hidden; pointer-events: none; }

@media (max-width: 800px) {
  .calendar-heading, .calendar-toolbar { align-items: stretch; flex-direction: column; }
  .calendar-scope-controls { align-items: flex-start; flex-direction: column; gap: 8px; }
  .calendar-legend { justify-content: flex-start; }
  .calendar-model-strip { align-items: flex-start; flex-direction: column; gap: 5px; }
  .calendar-model-pills { width: 100%; }
  .form-grid { grid-template-columns: 1fr; }
  .calendar-wrap, .modato-calendar { min-height: 680px; height: 680px; }
  :deep(.vuecal__title-bar) { padding: 0 8px; }
  :deep(.vuecal__title) { font-size: 17px; }
}
</style>
